import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { streamAiSupportSession, getAssistantWalkthrough, getDetailSession } from 'redux/actions/mvp/session.action';
import { createSpeechQueue } from './assistantSpeech';
import { startUtterance } from './prejoinSpeechInput';
import { createWalkthroughController } from './prejoinWalkthrough';
import './create-session.scss';
import '@zoom/videosdk-ui-toolkit/dist/videosdk-ui-toolkit.css';
import { getUiLocale, I18n } from '../../../../helpers/i18n';
import arrowBackIcon from '../../../../static/svg/back.svg';
import ReactMarkdown from 'react-markdown';
import Webcam from 'react-webcam';
import {
  AudioOutlined,
  CaretRightOutlined,
  CheckOutlined,
  PauseOutlined,
  RedoOutlined,
  ShrinkOutlined,
  StepForwardOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import {
  MVP_DEVICE_COMBOS,
  MVP_DEVICE_COMBO_I18N_KEYS,
  getDeviceComboStorageKey,
  savePrejoinMediaPrefs,
} from '../../../../constants/mvpDeviceCombos';

const walkthroughRenderers = {
  paragraph: ({ children }) => <span>{children}</span>,
};

function isCameraInUseMediaError(err) {
  const name = err?.name || '';
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return true;
  }
  const message = String(err?.message || err || '').toLowerCase();
  return message.includes('could not start video source') || message.includes('in use');
}

function resolvePrejoinMediaStatusMessage(micError, cameraError) {
  const noCamMsg = I18n.t('no_cam');
  if (micError && cameraError === noCamMsg) {
    return I18n.t('no_cam_mic');
  }
  if (micError) {
    return micError;
  }
  return cameraError || null;
}

function AiSupportSessionVideo({ user, sessionInfo, sessionId: sessionIdProp, joinSession, goBack, getDetailSession }) {
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [waveformStream, setWaveformStream] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const [devices, setDevices] = useState([]);
  const [activeDeviceId, setActiveDeviceId] = useState('');
  const [hasCamera, setHasCamera] = useState(false);
  const [error, setError] = useState(null);
  const [micError, setMicError] = useState(null);
  const [confirmedDeviceCombo, setConfirmedDeviceCombo] = useState(null);
  const [isEditingDeviceCombo, setIsEditingDeviceCombo] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCamOn, setIsCamOn] = useState(true);
  const [walkthroughLoading, setWalkthroughLoading] = useState(false);
  const [walkthroughView, setWalkthroughView] = useState(null);
  const [walkthroughExpanded, setWalkthroughExpanded] = useState(true);

  const conversationIdRef = useRef('');
  const parentMessageIdRef = useRef('');
  const messagesRef = useRef([]);
  const isSendingRef = useRef(false);
  const chatContentRef = useRef(null);
  const webcamRef = useRef(null);
  const camStreamRef = useRef(null);
  const confirmedDeviceComboRef = useRef(null);
  const micStreamRef = useRef(null);
  const isMicOnRef = useRef(true);
  const isCamOnRef = useRef(true);
  const activeDeviceIdRef = useRef('');
  const speechPlayerRef = useRef(null);
  const utteranceRef = useRef(null);
  const waveformRef = useRef(null);
  const barsRef = useRef(null);
  const listenStreamRef = useRef(null);
  const walkthroughRef = useRef(null);
  const isListeningRef = useRef(false);
  const speechEndedAtRef = useRef(0);
  const voiceLatencyStartRef = useRef(0);

  const sessionType = sessionInfo?.sessionType || null;
  // Prefer route id so handoff key matches session-video/{id}.
  const mediaSessionId =
    sessionIdProp || sessionInfo?._id || sessionInfo?.sessionUserId || '';
  const sessionStorageKey = useMemo(
    () => getDeviceComboStorageKey(sessionInfo?._id || sessionInfo?.sessionUserId),
    [sessionInfo?._id, sessionInfo?.sessionUserId],
  );

  useEffect(() => {
    confirmedDeviceComboRef.current = confirmedDeviceCombo;
  }, [confirmedDeviceCombo]);

  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    isMicOnRef.current = isMicOn;
  }, [isMicOn]);

  useEffect(() => {
    isCamOnRef.current = isCamOn;
  }, [isCamOn]);

  useEffect(() => {
    activeDeviceIdRef.current = activeDeviceId;
  }, [activeDeviceId]);

  const persistMediaPrefs = useCallback(
    (next = {}) => {
      if (!mediaSessionId) return;
      savePrejoinMediaPrefs(mediaSessionId, {
        audio: next.audio != null ? next.audio : isMicOnRef.current,
        video: next.video != null ? next.video : isCamOnRef.current,
        videoDeviceId: next.videoDeviceId != null ? next.videoDeviceId : activeDeviceIdRef.current,
      });
    },
    [mediaSessionId],
  );

  useEffect(() => {
    setIsEditingDeviceCombo(false);

    if (!sessionStorageKey) {
      setConfirmedDeviceCombo(null);
      return;
    }

    try {
      const stored = sessionStorage.getItem(sessionStorageKey);
      if (stored && MVP_DEVICE_COMBOS.includes(stored)) {
        setConfirmedDeviceCombo(stored);
      } else {
        setConfirmedDeviceCombo(null);
      }
    } catch {
      setConfirmedDeviceCombo(null);
    }
  }, [sessionStorageKey]);

  const stopMicPreview = useCallback(() => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
  }, []);

  const stopCamPreview = useCallback(() => {
    if (camStreamRef.current) {
      camStreamRef.current.getTracks().forEach((track) => track.stop());
      camStreamRef.current = null;
    }
    const fromWebcam = webcamRef.current && webcamRef.current.stream;
    if (fromWebcam && typeof fromWebcam.getTracks === 'function') {
      fromWebcam.getTracks().forEach((track) => track.stop());
    }
  }, []);

  useEffect(() => {
    if (!isCamOn) {
      stopCamPreview();
    }
  }, [isCamOn, stopCamPreview]);

  useEffect(() => {
    return () => {
      stopCamPreview();
    };
  }, [stopCamPreview]);

  const startMicPreview = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setIsMicOn(false);
      setMicError(I18n.t('no_mic'));
      persistMediaPrefs({ audio: false });
      return false;
    }
    try {
      stopMicPreview();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      setMicError(null);
      return true;
    } catch (err) {
      console.log(err);
      setIsMicOn(false);
      setMicError(I18n.t('no_mic'));
      persistMediaPrefs({ audio: false });
      return false;
    }
  }, [persistMediaPrefs, stopMicPreview]);

  useEffect(() => {
    if (isMicOn) {
      startMicPreview();
    } else {
      stopMicPreview();
    }
    return () => {
      stopMicPreview();
    };
  }, [isMicOn, startMicPreview, stopMicPreview]);

  useEffect(() => {
    persistMediaPrefs();
  }, [isMicOn, isCamOn, activeDeviceId, persistMediaPrefs]);

  const releaseListenStream = () => {
    if (!listenStreamRef.current) return;
    listenStreamRef.current.getTracks().forEach((track) => track.stop());
    listenStreamRef.current = null;
  };

  const stopUtterance = () => {
    if (utteranceRef.current) {
      utteranceRef.current.stop();
      utteranceRef.current = null;
    }
    releaseListenStream();
    setWaveformStream(null);
    setIsListening(false);
    setLiveTranscript('');
  };

  const resetAiConversation = () => {
    stopUtterance();
    conversationIdRef.current = '';
    parentMessageIdRef.current = '';
    messagesRef.current = [];
    setMessages([]);
  };

  const handleDeviceComboSelect = (combo) => {
    const previousCombo = confirmedDeviceComboRef.current;
    const isComboChange = Boolean(previousCombo && previousCombo !== combo);

    if (isComboChange) {
      resetAiConversation();
      setWalkthroughExpanded(true);
    } else if (messagesRef.current.length > 0) {
      if (walkthroughRef.current) walkthroughRef.current.pause();
      setWalkthroughExpanded(false);
    }

    setConfirmedDeviceCombo(combo);
    setIsEditingDeviceCombo(false);
    if (sessionStorageKey) {
      try {
        sessionStorage.setItem(sessionStorageKey, combo);
      } catch {
        // ignore quota / private mode
      }
    }
  };

  const showDeviceComboPicker = !confirmedDeviceCombo || isEditingDeviceCombo;
  const showDeviceComboSummary = Boolean(confirmedDeviceCombo && !isEditingDeviceCombo);
  const canUseAiChat = Boolean(sessionType && confirmedDeviceCombo);
  const walkthroughOpen = Boolean(
    walkthroughView && !walkthroughView.closed && walkthroughView.step && !isEditingDeviceCombo,
  );

  useEffect(() => {
    if (messages.length > 0) {
      if (walkthroughRef.current) walkthroughRef.current.pause();
      setWalkthroughExpanded(false);
    }
  }, [messages.length]);

  useEffect(() => {
    if (isEditingDeviceCombo && walkthroughRef.current) {
      walkthroughRef.current.pause();
    }
  }, [isEditingDeviceCombo]);

  useEffect(() => {
    let cancelled = false;
    if (walkthroughRef.current) {
      walkthroughRef.current.stop();
      walkthroughRef.current = null;
    }
    setWalkthroughView(null);

    if (!sessionType || !confirmedDeviceCombo) {
      setWalkthroughLoading(false);
      return undefined;
    }

    setWalkthroughLoading(true);
    getAssistantWalkthrough(sessionType, confirmedDeviceCombo).then((data) => {
      if (cancelled) return;
      setWalkthroughLoading(false);
      const steps = data && Array.isArray(data.steps) ? data.steps : [];
      if (!steps.length) return;
      const controller = createWalkthroughController({
        steps,
        onChange: (state) => {
          if (cancelled) return;
          setWalkthroughView(state);
        },
      });
      walkthroughRef.current = controller;
      controller.start();
      if (isListeningRef.current) controller.pause();
    }).catch(() => {
      if (cancelled) return;
      setWalkthroughLoading(false);
    });

    return () => {
      cancelled = true;
      if (walkthroughRef.current) {
        walkthroughRef.current.stop();
        walkthroughRef.current = null;
      }
    };
  }, [sessionType, confirmedDeviceCombo]);

  const renderers = {
    image: ({ src, alt, title }) => (
      <img
        src={src}
        alt={alt}
        title={title}
        style={{ maxWidth: '100%', cursor: 'zoom-in', margin: '10px 0' }}
        onClick={() => setZoomImage(src)}
      />
    ),
  };

  const appendMessage = (message) => {
    messagesRef.current = [...messagesRef.current, message];
    setMessages(messagesRef.current);
  };

  const updateSpeech = (messageId, patch) => {
    messagesRef.current = messagesRef.current.map((message) => {
      if (!message.speech || message.speech.messageId !== messageId) return message;
      if (message.speech.phase === 'done' && patch.phase !== 'done') return message;
      return { ...message, speech: { ...message.speech, ...patch } };
    });
    setMessages(messagesRef.current);
  };

  const stopSpeech = () => {
    if (speechPlayerRef.current) speechPlayerRef.current.stop();
    messagesRef.current = messagesRef.current.map((message) => {
      if (!message.speech || message.speech.phase === 'done' || message.speech.phase === 'error') {
        return message;
      }
      // Dừng TTS giữa chừng: hiện full answer (đã có từ onDone) thay vì chữ đang reveal.
      const fullText = message.text || message.speech.revealed || '';
      return {
        ...message,
        text: fullText,
        speech: { ...message.speech, phase: 'done', revealed: fullText },
      };
    });
    setMessages(messagesRef.current);
  };

  useEffect(() => () => {
    if (speechPlayerRef.current) speechPlayerRef.current.stop();
    if (utteranceRef.current) utteranceRef.current.stop();
    releaseListenStream();
  }, []);

  useEffect(() => {
    if (!isListening) return undefined;
    const stream = waveformStream;
    const canvas = waveformRef.current;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!stream || !canvas || !AudioCtx) return undefined;

    let frame = 0;
    let ctx;
    try {
      ctx = new AudioCtx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const samples = new Uint8Array(analyser.fftSize);
      const freq = new Uint8Array(analyser.frequencyBinCount);
      const barCount = 5;
      const barLevels = new Array(barCount).fill(0);
      let smoothed = 0;
      const draw = () => {
        const node = waveformRef.current;
        if (!node) return;
        const width = node.width;
        const height = node.height;
        const pen = node.getContext('2d');
        analyser.getByteTimeDomainData(samples);
        analyser.getByteFrequencyData(freq);
        let level = 0;
        for (let i = 0; i < samples.length; i += 1) level += Math.abs(samples[i] - 128);
        level = Math.min(1, (level / samples.length) / 28);
        smoothed = smoothed * 0.65 + level * 0.35;
        const bars = barsRef.current ? barsRef.current.children : [];
        const voiceStart = 1;
        const voiceEnd = Math.min(freq.length, 41);
        const group = Math.max(1, Math.floor((voiceEnd - voiceStart) / 3));
        for (let b = 0; b < barCount; b += 1) {
          const start = voiceStart + Math.abs(b - 2) * group;
          let sum = 0;
          for (let i = start; i < start + group && i < voiceEnd; i += 1) sum += freq[i];
          const band = Math.min(1, (sum / group) / 140);
          barLevels[b] = barLevels[b] * 0.55 + band * 0.45;
          if (bars[b]) bars[b].style.transform = `scaleY(${0.28 + barLevels[b] * 0.72})`;
        }
        pen.clearRect(0, 0, width, height);
        const cx = width / 2;
        const cy = height / 2;
        const rings = [
          { radius: 36 + smoothed * 4, color: 'rgba(255, 246, 236, 0.95)' },
          { radius: 31 + smoothed * 2, color: 'rgba(242, 190, 140, 0.96)' },
        ];
        rings.forEach((ring) => {
          pen.beginPath();
          pen.fillStyle = ring.color;
          pen.arc(cx, cy, ring.radius, 0, Math.PI * 2);
          pen.fill();
        });
        frame = requestAnimationFrame(draw);
      };
      draw();
      return () => {
        cancelAnimationFrame(frame);
        const node = waveformRef.current;
        if (node) node.getContext('2d').clearRect(0, 0, node.width, node.height);
        source.disconnect();
        ctx.close();
      };
    } catch (err) {
      if (ctx && ctx.close) ctx.close();
      return undefined;
    }
  }, [isListening, waveformStream]);

  const patchAssistant = (messageId, patch) => {
    messagesRef.current = messagesRef.current.map((message) => {
      if (!message.speech || message.speech.messageId !== messageId) return message;
      return { ...message, ...patch };
    });
    setMessages(messagesRef.current);
  };

  const logVoiceLatency = () => {
    const startedAt = voiceLatencyStartRef.current;
    if (!startedAt) return;
    voiceLatencyStartRef.current = 0;
    const elapsedMs = Date.now() - startedAt;
    console.log(`[setup voice] user finished speaking → audio start: ${elapsedMs} ms`);
  };

  const sendQuery = async (rawText) => {
    const query = (rawText || '').trim();
    const deviceCombo = confirmedDeviceComboRef.current;

    if (!query || !sessionType || !deviceCombo || isSendingRef.current) {
      voiceLatencyStartRef.current = 0;
      return;
    }

    isSendingRef.current = true;
    setIsSending(true);
    stopSpeech();
    appendMessage({ role: 'user', text: query });

    let queue = null;
    let spokenMessageId = null;
    try {
      await streamAiSupportSession(
        query,
        user.name,
        { sessionType, deviceCombo, sessionUserId: sessionInfo?.sessionUserId || '' },
        conversationIdRef.current,
        parentMessageIdRef.current,
        {
          onAudio: (segment) => {
            if (!segment || !segment.audio || !segment.messageId) return;
            if (!queue) {
              spokenMessageId = segment.messageId;
              appendMessage({
                role: 'assistant',
                text: '',
                speech: { messageId: segment.messageId, phase: 'loading', revealed: '' },
              });
              queue = createSpeechQueue();
              speechPlayerRef.current = queue;
              queue.push(segment);
              queue.start({
                onReveal: (revealed) => updateSpeech(spokenMessageId, { revealed, phase: 'playing' }),
                onPhase: (phase) => updateSpeech(spokenMessageId, { phase }),
                onPlaybackStart: logVoiceLatency,
              });
              return;
            }
            queue.push(segment);
          },
          onDone: (data) => {
            const payload = data || {};
            if (payload.conversation_id) conversationIdRef.current = payload.conversation_id;
            if (payload.message_id) parentMessageIdRef.current = payload.message_id;
            if (queue && spokenMessageId) {
              patchAssistant(spokenMessageId, { text: payload.answer || '' });
              queue.close();
              return;
            }
            voiceLatencyStartRef.current = 0;
            appendMessage({ role: 'assistant', text: payload.answer || '' });
          },
          onError: () => {
            voiceLatencyStartRef.current = 0;
            if (queue) queue.stop();
            else appendMessage({ role: 'error', text: I18n.t('something_wrong') });
          },
        },
      );
    } catch (err) {
      voiceLatencyStartRef.current = 0;
      if (!queue) appendMessage({ role: 'error', text: I18n.t('something_wrong') });
      else if (speechPlayerRef.current) speechPlayerRef.current.stop();
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  const closeWalkthrough = () => {
    if (walkthroughRef.current) walkthroughRef.current.closeGuide();
    setWalkthroughExpanded(false);
  };

  const resumeWalkthrough = () => {
    setWalkthroughExpanded(true);
    if (walkthroughRef.current) walkthroughRef.current.start();
  };

  const beginUtterance = () => {
    // User hỏi tiếp lúc AI đang đọc → dừng audio + hiện hết câu trả lời.
    stopSpeech();
    if (walkthroughRef.current) walkthroughRef.current.pause();
    setWalkthroughExpanded(false);
    setMicError(null);
    setLiveTranscript('');
    setIsListening(true);
    utteranceRef.current = startUtterance({
      lang: getUiLocale(),
      onPartial: (text) => {
        speechEndedAtRef.current = Date.now();
        setLiveTranscript(text);
      },
      onFinal: (text) => {
        utteranceRef.current = null;
        releaseListenStream();
        setWaveformStream(null);
        setIsListening(false);
        setLiveTranscript('');
        if (text) {
          voiceLatencyStartRef.current = speechEndedAtRef.current || Date.now();
          sendQuery(text);
        }
      },
      onError: () => {
        utteranceRef.current = null;
        releaseListenStream();
        setWaveformStream(null);
        setIsListening(false);
        setLiveTranscript('');
        setMicError(I18n.t('prejoin_speech_unsupported'));
      },
    });
  };

  const handleTalk = () => {
    if (!canUseAiChat || isSendingRef.current) return;

    if (isListening) {
      if (utteranceRef.current) utteranceRef.current.stop({ send: true });
      if (utteranceRef.current) stopUtterance();
      return;
    }

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        if (!utteranceRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        listenStreamRef.current = stream;
        setWaveformStream(stream);
      }).catch(() => {});
    }
    beginUtterance();
  };

  useEffect(() => {
    const node = chatContentRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [messages, isSending, isListening, liveTranscript]);

  const walkthroughWasExpandedRef = useRef(walkthroughExpanded);
  useEffect(() => {
    const opened = walkthroughExpanded && !walkthroughWasExpandedRef.current;
    walkthroughWasExpandedRef.current = walkthroughExpanded;
    if (!opened) return;
    const node = chatContentRef.current;
    if (node) node.scrollTop = 0;
  }, [walkthroughExpanded]);

  const handleDevices = useCallback((mediaDevices) => {
    const videoDevices = mediaDevices.filter(({ kind }) => kind === 'videoinput');
    setDevices(videoDevices);
    setHasCamera(videoDevices.length > 0);

    if (videoDevices.length > 0) {
      setActiveDeviceId((prev) => prev || videoDevices[0].deviceId);
      setError(null);
    } else {
      setIsCamOn(false);
      setError(I18n.t('no_cam'));
    }
  }, []);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices()
      .then(handleDevices)
      .catch((err) => {
        console.log(err);
        setError(I18n.t('no_cam'));
        setHasCamera(false);
        setIsCamOn(false);
      });
  }, [handleDevices]);

  const refreshCameraDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return false;
    }
    try {
      const mediaDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = mediaDevices.filter(({ kind }) => kind === 'videoinput');
      setDevices(videoDevices);
      setHasCamera(videoDevices.length > 0);
      if (videoDevices.length > 0) {
        setActiveDeviceId((prev) => prev || videoDevices[0].deviceId);
        return true;
      }
      return false;
    } catch (err) {
      console.log(err);
      setHasCamera(false);
      return false;
    }
  }, []);

  // Xin quyền camera khi chưa enumerate được device (thường do chưa grant permission)
  const requestingCameraRef = useRef(false);
  const requestCameraAccess = useCallback(async () => {
    if (requestingCameraRef.current) return false;
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(I18n.t('no_cam'));
      setIsCamOn(false);
      persistMediaPrefs({ video: false });
      return false;
    }

    requestingCameraRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Dừng stream tạm — Webcam sẽ lấy lại preview khi mount
      stream.getTracks().forEach((track) => track.stop());

      const found = await refreshCameraDevices();
      if (!found) {
        setError(I18n.t('no_cam'));
        setIsCamOn(false);
        persistMediaPrefs({ video: false });
        return false;
      }

      setError(null);
      setIsCamOn(true);
      persistMediaPrefs({ video: true });
      return true;
    } catch (err) {
      console.log(err);
      setError(I18n.t(isCameraInUseMediaError(err) ? 'camera_in_use' : 'no_cam'));
      setIsCamOn(false);
      persistMediaPrefs({ video: false });
      await refreshCameraDevices();
      return false;
    } finally {
      requestingCameraRef.current = false;
    }
  }, [persistMediaPrefs, refreshCameraDevices]);

  const switchCamera = (deviceId) => {
    setActiveDeviceId(deviceId);
  };

  const toggleMic = () => {
    setIsMicOn((prev) => {
      const next = !prev;
      if (!next) {
        setMicError(null);
      }
      return next;
    });
  };

  const toggleCam = () => {
    if (isCamOn) {
      setIsCamOn(false);
      // keep last camera error only when no device; clear permission noise when user turns off
      if (hasCamera) {
        setError(null);
      }
      return;
    }
    if (!hasCamera) {
      requestCameraAccess();
      return;
    }
    setIsCamOn(true);
  };

  const videoConstraints = {
    deviceId: activeDeviceId ? { exact: activeDeviceId } : undefined,
    facingMode: activeDeviceId ? undefined : 'user',
  };

  const isEnded = sessionInfo?.state === 'is_ended' || sessionInfo?.infoExtra?.state === 'is_ended';
  const canJoin =
    !isEnded &&
    (sessionInfo?.state === 'is_processing' || sessionInfo?.infoExtra?.state === 'is_processing');

  const handleJoinClick = () => {
    if (!canJoin) return;
    persistMediaPrefs();
    stopMicPreview();
    joinSession();
  };

  const showWebcam = isCamOn && hasCamera;
  const statusMessage = resolvePrejoinMediaStatusMessage(micError, error);

  const honorificName = user?.type === 'senior'
    ? I18n.t('senior_honorific')
    : I18n.t('buddy_honorific');

  return (
    <div className="container ai-support-session">
      <img
        className="back"
        src={arrowBackIcon}
        alt="Arrow Back"
        onClick={() => goBack()}
      />
      <div className="body">
        <div className="d-flex w-100 prejoin-layout">
          <div className="left">
            <div className="webcam-container">
              {statusMessage && (
                <div className="ml-3 error-message prejoin-media-error">
                  <p>{statusMessage}</p>
                </div>
              )}

              {!hasCamera && !error && isCamOn && (
                <div className="ml-3 no-camera-message">
                  <p>{I18n.t('check_camera')}</p>
                </div>
              )}

              {devices.length > 1 && isCamOn && hasCamera && (
                <div className="camera-selector">
                  <select
                    onChange={(e) => switchCamera(e.target.value)}
                    value={activeDeviceId}
                  >
                    {devices.map((device, index) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || I18n.t('camera_n', { n: index + 1 })}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {showWebcam ? (
                <Webcam
                  className="prejoin-webcam"
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover',
                  }}
                  audio={false}
                  ref={webcamRef}
                  videoConstraints={videoConstraints}
                  onUserMedia={(stream) => {
                    camStreamRef.current = stream;
                    setError(null);
                  }}
                  onUserMediaError={(err) => {
                    console.log(err);
                    setError(
                      I18n.t(isCameraInUseMediaError(err) ? 'camera_in_use' : 'no_cam')
                    );
                    setIsCamOn(false);
                    persistMediaPrefs({ video: false });
                  }}
                  mirrored={true}
                />
              ) : (
                <div className="prejoin-webcam-placeholder" aria-hidden="true">
                  <div className="prejoin-webcam-avatar">
                    <UserOutlined />
                  </div>
                </div>
              )}

              <div className="prejoin-media-controls">
                <button
                  type="button"
                  className={[
                    'prejoin-media-btn',
                    isMicOn ? 'prejoin-media-btn--on' : 'prejoin-media-btn--off',
                  ].join(' ')}
                  onClick={toggleMic}
                  aria-pressed={isMicOn}
                  aria-label={isMicOn ? I18n.t('video_mute_audio') : I18n.t('video_unmute_audio')}
                  title={isMicOn ? I18n.t('video_mute_audio') : I18n.t('video_unmute_audio')}
                >
                  <span className="prejoin-media-btn__icon">
                    <AudioOutlined />
                    {!isMicOn && <span className="prejoin-media-btn__slash" />}
                  </span>
                  <span className="prejoin-media-btn__label">{I18n.t('prejoin_mic_label')}</span>
                </button>
                <button
                  type="button"
                  className={[
                    'prejoin-media-btn',
                    isCamOn ? 'prejoin-media-btn--on' : 'prejoin-media-btn--off',
                  ].join(' ')}
                  onClick={toggleCam}
                  aria-pressed={isCamOn}
                  aria-label={isCamOn ? I18n.t('video_stop_video') : I18n.t('video_start_video')}
                  title={isCamOn ? I18n.t('video_stop_video') : I18n.t('video_start_video')}
                >
                  <span className="prejoin-media-btn__icon">
                    <VideoCameraOutlined />
                    {!isCamOn && <span className="prejoin-media-btn__slash" />}
                  </span>
                  <span className="prejoin-media-btn__label">{I18n.t('prejoin_camera_label')}</span>
                </button>
              </div>
            </div>
          </div>
          <div className="right">
            <div className="prejoin-right__header">
              <span className="title">{I18n.t('help_setting_device')}</span>

              {sessionType ? (
                <div className="prejoin-device-combo">
                  {showDeviceComboPicker ? (
                    <>
                      <p className="prejoin-device-combo__label">{I18n.t('prejoin_device_combo_label')}</p>
                      <div className="prejoin-device-combo__options">
                        {MVP_DEVICE_COMBOS.map((combo) => {
                          const isSelected = confirmedDeviceCombo === combo;
                          return (
                            <button
                              key={combo}
                              type="button"
                              className={[
                                'prejoin-device-combo__option',
                                isSelected ? 'prejoin-device-combo__option--selected' : '',
                              ].filter(Boolean).join(' ')}
                              onClick={() => handleDeviceComboSelect(combo)}
                            >
                              {I18n.t(MVP_DEVICE_COMBO_I18N_KEYS[combo])}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : null}
                  {showDeviceComboSummary ? (
                    <div className="prejoin-device-combo__selected">
                      <span className="prejoin-device-combo__selected-text">
                        {I18n.t('prejoin_device_combo_selected', {
                          device: I18n.t(MVP_DEVICE_COMBO_I18N_KEYS[confirmedDeviceCombo]),
                        })}
                      </span>
                      <button
                        type="button"
                        className="prejoin-device-combo__change"
                        onClick={() => setIsEditingDeviceCombo(true)}
                      >
                        {I18n.t('prejoin_device_combo_change')}
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="prejoin-session-type prejoin-session-type--missing">
                  {I18n.t('prejoin_session_type_missing')}
                </p>
              )}

              {canUseAiChat && !messages.length && !walkthroughOpen && !walkthroughLoading ? (
                <span className="prejoin-prompt">{I18n.t('difficulty_facing', { name: honorificName })}</span>
              ) : null}
              {walkthroughOpen && !walkthroughExpanded ? (
                <div className="prejoin-walkthrough prejoin-walkthrough--collapsed">
                  <ReactMarkdown className="prejoin-walkthrough__text" renderers={walkthroughRenderers}>
                    {walkthroughView.step.text}
                  </ReactMarkdown>
                  <div className="prejoin-walkthrough__bar">
                    <button
                      type="button"
                      className="prejoin-walkthrough__action prejoin-walkthrough__action--primary"
                      aria-label={I18n.t('prejoin_walkthrough_resume')}
                      onClick={resumeWalkthrough}
                    >
                      <CaretRightOutlined />
                      <span>{I18n.t('prejoin_walkthrough_resume')}</span>
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="prejoin-right__scroll" ref={chatContentRef}>
              {walkthroughOpen && walkthroughExpanded ? (
                <div className="prejoin-walkthrough">
                  {walkthroughView.step.image ? (
                    <button
                      type="button"
                      className="prejoin-walkthrough__image"
                      onClick={() => setZoomImage(walkthroughView.step.image)}
                    >
                      <img src={walkthroughView.step.image} alt={walkthroughView.step.id} />
                    </button>
                  ) : null}
                  <ReactMarkdown className="prejoin-walkthrough__text" renderers={walkthroughRenderers}>
                    {walkthroughView.step.text}
                  </ReactMarkdown>
                  <div className="prejoin-walkthrough__controls">
                    <button
                      type="button"
                      className="prejoin-walkthrough__action prejoin-walkthrough__action--primary"
                      aria-label={walkthroughView.playing ? I18n.t('prejoin_walkthrough_pause') : I18n.t('prejoin_walkthrough_play')}
                      onClick={() => (walkthroughView.playing ? walkthroughRef.current.pause() : walkthroughRef.current.start())}
                    >
                      {walkthroughView.playing ? <PauseOutlined /> : <CaretRightOutlined />}
                      <span>{walkthroughView.playing ? I18n.t('prejoin_walkthrough_pause') : I18n.t('prejoin_walkthrough_play')}</span>
                    </button>
                    <button
                      type="button"
                      className="prejoin-walkthrough__action"
                      aria-label={I18n.t('prejoin_walkthrough_repeat')}
                      onClick={() => walkthroughRef.current.repeat()}
                    >
                      <RedoOutlined />
                      <span>{I18n.t('prejoin_walkthrough_repeat')}</span>
                    </button>
                    <button
                      type="button"
                      className="prejoin-walkthrough__action"
                      aria-label={walkthroughView.isLast ? I18n.t('prejoin_walkthrough_done') : I18n.t('prejoin_walkthrough_next')}
                      onClick={() => walkthroughRef.current.next()}
                    >
                      {walkthroughView.isLast ? <CheckOutlined /> : <StepForwardOutlined />}
                      <span>{walkthroughView.isLast ? I18n.t('prejoin_walkthrough_done') : I18n.t('prejoin_walkthrough_next')}</span>
                    </button>
                    <button
                      type="button"
                      className="prejoin-walkthrough__action"
                      aria-label={I18n.t('prejoin_walkthrough_close')}
                      onClick={closeWalkthrough}
                    >
                      <ShrinkOutlined />
                      <span>{I18n.t('prejoin_walkthrough_close')}</span>
                    </button>
                  </div>
                </div>
              ) : null}

              {sessionType && (messages.length > 0 || isSending || isListening) && !(walkthroughOpen && walkthroughExpanded) && (
                <div className="prejoin-right__chat">
                  <div className="content">
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        className={`prejoin-message prejoin-message--${message.role}`}
                      >
                        {message.role === 'assistant' && message.speech && message.speech.phase !== 'done' && message.speech.phase !== 'error' ? (
                          <div>
                            {message.speech.revealed ? (
                              <p className="prejoin-tts-spoken" aria-live="polite">{message.speech.revealed}</p>
                            ) : (
                              <p className="prejoin-tts-reading">{I18n.t('prejoin_tts_reading')}</p>
                            )}
                          </div>
                        ) : message.role === 'assistant' ? (
                          <ReactMarkdown renderers={renderers} linkTarget="_blank">
                            {message.text}
                          </ReactMarkdown>
                        ) : (
                          message.text
                        )}
                      </div>
                    ))}
                    {isListening && (
                      <div className="prejoin-message prejoin-message--user">
                        {liveTranscript || I18n.t('prejoin_listening')}
                      </div>
                    )}
                    {isSending && (
                      <div className="prejoin-message prejoin-message--pending">
                        {I18n.t('prejoin_chat_sending')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {sessionType && (
              <div className="prejoin-composer">
                <div className="prejoin-composer__mic">
                  <canvas
                    ref={waveformRef}
                    className="prejoin-composer__wave"
                    width={140}
                    height={140}
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className={`prejoin-composer__talk${isListening ? ' prejoin-composer__talk--on' : ''}`}
                    aria-label={I18n.t('prejoin_tap_to_talk')}
                    aria-pressed={isListening}
                    disabled={!canUseAiChat || isSending}
                    onClick={handleTalk}
                  >
                    {isListening ? (
                      <span className="prejoin-composer__bars" ref={barsRef} aria-hidden="true">
                        <span />
                        <span />
                        <span />
                        <span />
                        <span />
                      </span>
                    ) : (
                      <AudioOutlined />
                    )}
                  </button>
                </div>
                <p className="prejoin-composer__caption" aria-live="polite">
                  {micError || (isListening ? I18n.t('prejoin_listening') : I18n.t('prejoin_tap_to_talk'))}
                </p>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={handleJoinClick}
          className="start"
          disabled={!canJoin}
        >
          {I18n.t('join_session_sort_title')}
        </button>
      </div>
      {zoomImage && (
        <div className="image-popup" onClick={() => setZoomImage(null)}>
          <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
            <img src={zoomImage} alt="Zoom" />
            <button className="close-btn" onClick={() => setZoomImage(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

const mapStateToProps = ({ user }) => ({ user });
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getDetailSession,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(AiSupportSessionVideo);
